import { metricLabel } from '../agent-common/diagnosisLabels';
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { getMe, type EvidenceBar, type Me } from '../../apis';
import { claimKey, type Claim } from './claims';
import { pinCandidates, pinDestination, reviewHasKey, reviewOfSection, reviewTakes, sectionTitle } from '../../stores/slices/reviewsSlice';
import { restsOnAReading } from '../../stores/slices/pinsSlice';
import { inclusiveEnd } from '../../stores/slices/landingAgentSlice';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import { stromaCompactPanel, stromaSectionLabel } from '../LandingAgent/askPresentation';

const BARS: EvidenceBar[] = ['conservative', 'balanced', 'exploratory'];
const BAR_HELP: Record<EvidenceBar, string> = { conservative: 'Confirmed', balanced: 'Confirmed + Emerging', exploratory: 'everything' };
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const Card = ({ title, children, testId }: { title: string; children: React.ReactNode; testId: string }) => (
  <Box data-testid={testId} sx={{ p: 1.5, ...stromaCompactPanel, borderColor: 'divider', display: 'grid', gap: 0.5 }}>
    <Typography variant="caption" sx={stromaSectionLabel}>{title}</Typography>
    {children}
  </Box>
);

/** The walk's number as a claim a review can take: the target, re-derived on the review's schedule. */
export function claimOfWalk(turn: LandingAgentTurn, perspective: string | null): Claim | null {
  const t = turn.result?.diagnose?.target;
  if (!t?.metric || !t.window_start || !t.window_end) return null;
  const name = String(t.metric).split('.').pop() || t.metric;
  return {
    id: `walk:${t.metric}`, label: (t.label || metricLabel(t.metric)), value: typeof t.current === 'number' ? t.current : null, format: t.format || null,
    confidence: 1, confidence_basis: 'the walk\'s own target', verdict: 'derived', caveats: [],
    grounding: { metric: name, metric_qid: t.metric, perspective: perspective || turn.perspectiveId || '', window: { start: String(t.window_start).slice(0, 10), end: inclusiveEnd(String(t.window_end)) },
      filters: Array.isArray(t.filters) ? t.filters : [], dimensions: [], row: null, definition: 'authored' } as any,
  };
}

/** Scroll a rung of THIS walk into view — the "This thread" nav; a thread may hold several walks. */
const jumpTo = (turnId: string, testId: string) => {
  const el = document.querySelector(`[data-turn-id="${turnId}"] [data-testid="${testId}"]`) || document.querySelector(`[data-testid="${testId}"]`);
  if (el && typeof (el as HTMLElement).scrollIntoView === 'function') (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/**
 * The walk tab's right rail (the mock): who is looking and through what
 * evidence policy, the risk tolerance that re-runs the walk at another bar,
 * the rungs of this thread, the living review the number lives in (or can
 * be pinned to), and the note that the tab saves itself.
 */
export default function WalkRail({ threadId, turn, latestTurn }: { threadId: string;
  /** the thread's latest walk — null on an analysis with no walk, which still has a number to add to a review */
  turn: LandingAgentTurn | null;
  /** the newest turn on screen when it is not the walk — a follow-up answer whose headline number can be pinned to the review too */
  latestTurn?: LandingAgentTurn | null }) {
  const thread = useVoyagerStore((s) => s.savedThreads.find((t) => t.id === threadId));
  const origin = useVoyagerStore((s) => (thread?.sourceKind === 'review_section' ? reviewOfSection(s.savedThreads, thread.sourceId) : null));
  const loadThread = useVoyagerStore((s) => s.loadThread);
  const raiseBar = useVoyagerStore((s) => s.raiseBar);
  const pinClaim = useVoyagerStore((s) => s.pinClaim);
  const perspective = turn?.perspectiveId || latestTurn?.perspectiveId || thread?.perspectiveId || null;
  // #1818: a number whose answer rests on a reading of a meaning no person has settled is never put on a review
  const walkClaim = turn && !restsOnAReading(turn.result?.ladder) ? claimOfWalk(turn, perspective) : null;
  const destination = useVoyagerStore((s) => pinDestination(s.savedThreads, s.currentThreadId, walkClaim?.grounding?.perspective));
  const walkOnReview = useVoyagerStore((s) => !!(walkClaim && destination && reviewHasKey(destination, claimKey(walkClaim))));
  const walkOnHome = useVoyagerStore((s) => !!(walkClaim && !destination && s.pins.some((p) => p.key === claimKey(walkClaim))));
  // The digging's own result: the headline number of the newest answer, when that answer is not the walk itself
  const followUp = latestTurn && latestTurn.id !== turn?.id && latestTurn.status === 'done' ? latestTurn : null;
  const followUpClaim: Claim | null = restsOnAReading(followUp?.result?.ladder) ? null : (followUp?.result?.ladder?.what?.tiles || []).find((c: Claim) => c?.grounding?.metric_qid && c.grounding.window?.start && c.grounding.window?.end) || null;
  // "on the review too" means THE review this analysis was opened from, when it takes the number — never
  // whichever living review happened to change last; a standalone walk falls back to the usual destination
  const pinClaimToReview = useVoyagerStore((s) => s.pinClaimToReview);
  const followUpDestination = useVoyagerStore((s) => (origin && reviewTakes(origin.review, followUpClaim?.grounding?.perspective)
    ? origin.review : pinDestination(s.savedThreads, s.currentThreadId, followUpClaim?.grounding?.perspective)));
  // "Pinned ✓" means on a REVIEW; a number that only reached the Home queue (no review takes its perspective) is not pinned to a review — the button greys out and says so
  const followUpOnReview = useVoyagerStore((s) => !!(followUpClaim && followUpDestination && reviewHasKey(followUpDestination, claimKey(followUpClaim))));
  const followUpOnHome = useVoyagerStore((s) => !!(followUpClaim && !followUpDestination && s.pins.some((p) => p.key === claimKey(followUpClaim))));
  const pinFollowUp = () => {
    if (!followUpClaim) return;
    const session = followUp?.sessionId || null;
    if (origin && followUpDestination?.id === origin.review.id) void pinClaimToReview(origin.review.id, followUpClaim, { thread_id: threadId, ...(session ? { session_id: session } : {}) });
    else pinClaim(followUpClaim, threadId, session);
  };
  // "Add to a review": the analysis's number — its latest answer's headline, else the walk's — goes to a
  // review the person picks, and the thread becomes that tile's analysis (listed there, resumed from there)
  const attachThreadToReview = useVoyagerStore((s) => s.attachThreadToReview);
  const attachClaim = followUpClaim || walkClaim;
  const candidates = useVoyagerStore((s) => pinCandidates(s.savedThreads, attachClaim?.grounding?.perspective));
  const [pick, setPick] = useState<string>('');
  const [attaching, setAttaching] = useState(false);
  const [attached, setAttached] = useState<string | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const attach = async () => {
    if (!attachClaim || !pick || attaching) return;
    setAttaching(true); setAttachError(null);
    const section = await attachThreadToReview(pick, attachClaim, threadId);
    setAttaching(false);
    // only both — the tile AND the back-link — is "added"; a partial failure keeps the picker so it can be tried again,
    // and shows THIS attempt's error (read from the store right after the call), never a stale one from another action
    if (section) setAttached(pick);
    else setAttachError(useVoyagerStore.getState().reviewsError || 'Could not add this analysis to the review');
  };
  const [me, setMe] = useState<Me | null>(null);
  useEffect(() => {
    let cancelled = false;
    getMe().then((m) => { if (!cancelled) setMe(m); }).catch(() => { if (!cancelled) setMe(null); });
    return () => { cancelled = true; };
  }, []);
  const policy = turn?.result?.diagnose?.policy;
  const gated = (policy?.policy || me?.evidence_policy.policy) === 'gated';
  const bar = (policy?.bar || me?.evidence_policy.bar || null) as EvidenceBar | null;
  const running = turn?.status === 'running';
  // WHAT NEXT is there when it has suggestions, a note, or suggestions the
  // ladder's policy withheld (raising the bar reveals them) — the ladder's
  // policy block counts those, not the walk's.
  const ladderPolicy = turn?.result?.ladder?.policy;
  const hasNext = !!(turn?.result?.ladder?.next?.length || turn?.result?.ladder?.next_note || ladderPolicy?.next_hidden || ladderPolicy?.next_withheld);

  return (
    <Box data-testid="walk-rail" sx={{ width: 236, flexShrink: 0, display: { xs: 'none', md: 'grid' }, gap: 1.25, alignContent: 'start', position: 'sticky', top: 0 }}>
      {turn && <>
      <Card title="Evidence policy" testId="rail-policy">
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{me ? `${me.display_name}${me.org_role ? ` · ${me.org_role}` : ''}` : '…'}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {me
            ? (me.evidence_policy.policy === 'gated' ? `Gated by default — only drivers that clear your bar surface; raise the bar to widen.`
              : me.evidence_policy.policy === 'facts' ? 'Facts only — confirmed evidence, no inference, WHAT NEXT withheld.'
              : 'Full — every driver, with its tier shown.')
            : 'Your evidence policy decides what an answer shows, never what you may reach.'}
        </Typography>
      </Card>
      <Card title="Risk tolerance" testId="rail-risk">
        <ToggleButtonGroup exclusive size="small" value={bar} fullWidth disabled={!gated || running}
          onChange={(_e, v: EvidenceBar | null) => { if (v && v !== bar) raiseBar(turn.id, v); }} aria-label="Risk tolerance">
          {BARS.map((b) => <ToggleButton key={b} value={b} sx={{ fontSize: '0.68rem', py: 0.5, textTransform: 'none' }} data-testid={`rail-bar-${b}`}>{cap(b)}</ToggleButton>)}
        </ToggleButtonGroup>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {gated ? `${cap(BARS[0])} shows only ${BAR_HELP.conservative}. ${cap(BARS[1])} adds Emerging. ${cap(BARS[2])} shows everything. Raising is for this answer only.`
            : 'Your policy is not gated: every driver is already shown, with its tier.'}
          {running ? ' Re-running the walk…' : ''}
        </Typography>
      </Card>
      <Card title="This thread" testId="rail-nav">
        {[['what-rung', 'What — deterministic answer', 'var(--accent)'], ['why-card', 'Why — drivers & matched events', 'var(--warn)'], ['next-card', 'What next — anchored suggestions', 'var(--ok)']].map(([id, label, color]) => (
          <Box key={id} role="button" tabIndex={0} onClick={() => jumpTo(turn.id, id)} onKeyDown={(e) => { if (e.key === 'Enter') jumpTo(turn.id, id); }}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', fontSize: 13, opacity: id === 'next-card' && !hasNext ? 0.5 : 1, '&:hover': { color: 'primary.dark' } }} data-testid={`rail-nav-${id}`}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />{label}
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13, color: 'text.disabled' }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'var(--dim)' }} />Act — approved & executed
        </Box>
      </Card>
      </>}
      <Card title="Pin to a living review" testId="rail-pin">
        {followUpClaim && (
          <Box sx={{ display: 'grid', gap: 0.5, mb: 1, pb: 1, borderBottom: 1, borderColor: 'divider' }} data-testid="rail-pin-followup">
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Your latest answer's number — {followUpClaim.label || followUpClaim.grounding?.metric} — can live on the review too, refreshed with it.
            </Typography>
            <Button size="small" variant={followUpOnReview ? 'contained' : 'outlined'} disabled={followUpOnReview || !followUpDestination} onClick={pinFollowUp} data-testid="rail-pin-followup-button">
              {followUpOnReview ? `Pinned to ${followUpDestination?.title || 'the review'} ✓` : followUpDestination ? `Pin ${followUpClaim.label || 'it'} to ${followUpDestination.title}` : 'No eligible review'}
            </Button>
            {!followUpDestination && (
              <Typography variant="caption" sx={{ color: 'text.disabled' }} data-testid="rail-pin-followup-none">
                {followUpOnHome ? 'Waiting on Home — no living review is grounded on this perspective yet.' : 'No living review is grounded on this perspective yet.'}
              </Typography>
            )}
          </Box>
        )}
        {origin ? (
          <>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Pinned ✓</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {sectionTitle(origin.section)} lives in {origin.review.title}. What this walk finds lands there as commentary.
            </Typography>
            <Button size="small" variant="outlined" onClick={() => loadThread(origin.review.id)} sx={{ mt: 0.5 }}>Open {origin.review.title}</Button>
          </>
        ) : walkClaim ? (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>This ladder becomes a tracked section of your review — refreshed on its schedule.</Typography>
            <Button size="small" variant={walkOnReview ? 'contained' : 'outlined'} disabled={walkOnReview || !destination} onClick={() => pinClaim(walkClaim, threadId, turn?.sessionId)} sx={{ mt: 0.5 }} data-testid="rail-pin-button">
              {walkOnReview ? `Pinned to ${destination?.title || 'the review'} ✓` : destination ? `Pin to ${destination.title}` : 'No eligible review'}
            </Button>
            {!destination && (
              <Typography variant="caption" sx={{ color: 'text.disabled' }} data-testid="rail-pin-none">
                {walkOnHome ? 'Waiting on Home — no living review is grounded on this perspective yet.' : 'No living review is grounded on this perspective yet.'}
              </Typography>
            )}
          </>
        ) : !attachClaim ? (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>An answer with a number can be added to a review once it lands.</Typography>
        ) : null}
        {!origin && attachClaim && (
          <Box sx={{ display: 'grid', gap: 0.75, mt: walkClaim ? 1 : 0 }} data-testid="rail-attach">
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Add this analysis to a review of your choosing: {attachClaim.label || attachClaim.grounding?.metric} becomes a tile there, and this thread its analysis.
            </Typography>
            {candidates.length === 0 ? (
              <Typography variant="caption" sx={{ color: 'text.disabled' }} data-testid="rail-attach-none">No living review is grounded on this perspective yet.</Typography>
            ) : attached ? (
              <Typography sx={{ fontSize: 13, fontWeight: 600 }} data-testid="rail-attached">Added to {candidates.find((c) => c.id === attached)?.title || 'the review'} ✓</Typography>
            ) : (
              <>
                <TextField select size="small" value={pick} onChange={(e) => setPick(e.target.value)} SelectProps={{ native: true }} inputProps={{ 'aria-label': 'Review to add this analysis to' }}>
                  <option value="">— choose a review —</option>
                  {candidates.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </TextField>
                <Button size="small" variant="outlined" disabled={!pick || attaching} onClick={attach} data-testid="rail-attach-button">{attaching ? 'Adding…' : 'Add to this review'}</Button>
                {attachError && <Typography variant="caption" color="error" data-testid="rail-attach-error">{attachError}</Typography>}
              </>
            )}
          </Box>
        )}
      </Card>
      <Card title="Saved automatically" testId="rail-saved">
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Leave this tab and come back — the conversation, its context, and its results are all still here. Star the tab to pin it to your profile.</Typography>
      </Card>
    </Box>
  );
}
